import { FC, useCallback, useContext, useMemo, useState } from 'react';
import { shallow } from 'zustand/shallow';
import { Flex, Heading, Text } from '@chakra-ui/react';
import theme from '@adflow/theme';
import type { AnimationEffect } from '@adflow/types';
import StoreContext from '../../../contexts/EditorContext';
import { useStore } from '../../../hooks/useStore';
import {
  ConflictingPresetError,
  DuplicatePresetError,
  PresetRoleError,
  getAnimationPreset,
  getPresetSelectionBlock,
  isAnimationPresetId,
} from '../../../animation';
import { AddAnimationMenu } from './AddAnimationMenu';
import { AppliedEffectEditor } from './AppliedEffectEditor';
import { listPresetsForRole } from './presetDisplay';

type Props = {
  targetId: string;
};

function blockedPresetIds(
  effects: readonly AnimationEffect[],
  targetId: string,
  effect: AnimationEffect,
): ReadonlySet<string> {
  const phase = getAnimationPreset(effect.presetId).phase;
  const blocked = new Set<string>();
  for (const preset of listPresetsForRole(phase)) {
    if (preset.id === effect.presetId) {
      continue;
    }
    if (getPresetSelectionBlock(effects, targetId, preset.id, effect.id) != null) {
      blocked.add(preset.id);
    }
  }
  return blocked;
}

const AnimateControls: FC<Props> = ({ targetId }) => {
  const { store: storeApi } = useContext(StoreContext);
  const store = useStore(
    (state) => ({
      animation: state.animation,
      applyAnimationEffect: state.applyAnimationEffect,
      updateAnimationEffect: state.updateAnimationEffect,
      changeAnimationEffectPreset: state.changeAnimationEffectPreset,
      removeAnimationEffect: state.removeAnimationEffect,
    }),
    shallow,
  );

  const effects = store.animation?.effects;
  const targetEffects = useMemo(
    () =>
      (effects ?? []).filter(
        (effect) => effect.targetId === targetId && isAnimationPresetId(effect.presetId),
      ),
    [effects, targetId],
  );
  const [openEffectIds, setOpenEffectIds] = useState<ReadonlySet<string>>(() => new Set());

  const handleApply = useCallback(
    (presetId: string) => {
      if (getPresetSelectionBlock(effects ?? [], targetId, presetId) != null) {
        return;
      }
      const previousIds = new Set(targetEffects.map((effect) => effect.id));
      try {
        store.applyAnimationEffect(targetId, presetId);
      } catch (error) {
        if (error instanceof DuplicatePresetError) {
          return;
        }
        throw error;
      }
      const added = storeApi
        ?.getState()
        .animation?.effects?.find(
          (effect) => effect.targetId === targetId && !previousIds.has(effect.id),
        );
      if (added) {
        setOpenEffectIds((current) => new Set(current).add(added.id));
      }
    },
    [effects, store, storeApi, targetEffects, targetId],
  );

  const handleChangePreset = useCallback(
    (effectId: string, presetId: string) => {
      try {
        store.changeAnimationEffectPreset(effectId, presetId);
      } catch (error) {
        if (
          error instanceof DuplicatePresetError ||
          error instanceof ConflictingPresetError ||
          error instanceof PresetRoleError
        ) {
          return;
        }
        throw error;
      }
    },
    [store],
  );

  const handleToggle = useCallback((effectId: string) => {
    setOpenEffectIds((current) => {
      const next = new Set(current);
      if (next.has(effectId)) {
        next.delete(effectId);
      } else {
        next.add(effectId);
      }
      return next;
    });
  }, []);

  return (
    <Flex direction="column" gap={4}>
      <Heading as="h3" size="sm" color={theme.palette.mcNavy.main}>
        Applied Animations
      </Heading>
      {targetEffects.length === 0 ? (
        <Text fontSize="sm" color="gray.500" data-test-id="applied-animations-empty">
          No animations on this layer yet.
        </Text>
      ) : (
        <Flex direction="column" gap={3}>
          {targetEffects.map((effect) => (
            <AppliedEffectEditor
              key={effect.id}
              effect={effect}
              isOpen={openEffectIds.has(effect.id)}
              blockedPresetIds={blockedPresetIds(effects ?? [], targetId, effect)}
              onToggle={() => handleToggle(effect.id)}
              onChangePreset={handleChangePreset}
              onUpdate={store.updateAnimationEffect}
              onRemove={store.removeAnimationEffect}
            />
          ))}
        </Flex>
      )}
      <AddAnimationMenu targetId={targetId} effects={effects ?? []} onApply={handleApply} />
    </Flex>
  );
};

AnimateControls.displayName = 'AnimateControls';

export default AnimateControls;
